import React, { useState } from "react";
import { ArrowRight, ArrowLeft, EyeOff, Glasses } from "lucide-react";
import { toast } from "sonner";

/**
 * Refrazione avanzata (dati in prescrizione_occhiali.avanzata, additivi retro-compatibili):
 * - part="lenti": "Utilizza lenti a tempiale/occhiali?" No/Sì → lenti in uso (L/I/V) + Visus CSL; meta (LAC, dominante, ambliopia)
 * - part="prescrizione": Nuova prescrizione Lontano/Intermedio/Vicino con visus corretto, Add/SF, copia OD→OS
 * Selettori: step 0,25, range ±20, asse 0-180, rotellina SOLO su campo focalizzato (no modifiche accidentali).
 */
const range = (from, to, step) => {
  const out = [];
  for (let v = from; v <= to + 1e-9; v += step) out.push(Math.round(v * 100) / 100);
  return out;
};
const fmtD = (n) => (n > 0 ? `+${n.toFixed(2)}` : n.toFixed(2));
export const SPHERE_OPTIONS = range(-20, 20, 0.25).map(fmtD);
export const CYL_OPTIONS = range(-20, 20, 0.25).map(fmtD);
export const AXIS_OPTIONS = range(0, 180, 1).map(String);
export const ADD_OPTIONS = range(0.25, 4, 0.25).map((n) => `+${n.toFixed(2)}`);
export const ACUITY_OPTIONS = [
  "10/10", "9/10", "8/10", "7/10", "6/10", "5/10", "4/10", "3/10", "2/10", "1/10",
  "1/20", "1/30", "1/60", "1/120", "conta dita", "moto mano", "percezione luce", "visus spento",
];
const DISTANCES = [
  { k: "lontano", label: "Per lontano (PL)" },
  { k: "intermedio", label: "Intermedio" },
  { k: "vicino", label: "Per vicino (PV)" },
];

export function WheelSelect({ value, onChange, options, disabled, testid, ph }) {
  const ref = React.useRef(null);
  const stateRef = React.useRef({ value, options, onChange, disabled });
  stateRef.current = { value, options, onChange, disabled };
  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // listener nativo non-passivo: consente preventDefault → niente scroll pagina
    const onWheel = (e) => {
      const { value, options, onChange, disabled } = stateRef.current;
      if (disabled || document.activeElement !== el) return;
      e.preventDefault();
      const idx = options.indexOf(value);
      const next = idx < 0 ? 0 : Math.min(options.length - 1, Math.max(0, idx + (e.deltaY > 0 ? 1 : -1)));
      onChange(options[next]);
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);
  return (
    <select ref={ref} value={value || ""} disabled={disabled} data-testid={testid}
      onChange={(e) => onChange(e.target.value)}
      className="w-full px-1.5 py-1.5 rounded border border-[#E2E8F0] text-xs font-mono text-center bg-white focus:outline-none focus:border-brand-azure focus:ring-1 focus:ring-brand-azure/40 disabled:bg-slate-100 disabled:text-slate-400">
      <option value="">{ph || "—"}</option>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}

function EyeTable({ title, cols, getCell, setCell, readOnly, isExcluded, onCopyRow, prefix }) {
  // Layout CSS grid con DOM in ordine colonna (OD→OS per campo): TAB segue l'ordine
  // visivo orizzontale (OD Sfera → OS Sfera → OD Cilindro → …). Shift+TAB inverso.
  // I campi disabilitati (occhio escluso / readOnly) vengono saltati nativamente dal browser.
  const template = `44px repeat(${cols.length}, minmax(78px, 1fr))${onCopyRow ? " 44px" : ""}`;
  const eyes = ["OD", "OS"];
  return (
    <div className="mb-3" data-testid={prefix}>
      {title && <div className="text-[11px] font-extrabold uppercase tracking-wider text-brand-navy mb-1">{title}</div>}
      <div className="overflow-x-auto">
        <div className="grid gap-x-1 gap-y-1.5 items-center min-w-[520px] text-xs" style={{ gridTemplateColumns: template }}>
          <div className="text-left text-slate-500 text-[10px] uppercase tracking-wider font-bold py-1" style={{ gridRow: 1, gridColumn: 1 }}>Occhio</div>
          {cols.map((c, i) => (
            <div key={`h-${c.k}`} className="text-center text-slate-500 text-[10px] uppercase tracking-wider font-bold px-1 py-1" style={{ gridRow: 1, gridColumn: i + 2 }}>{c.label}</div>
          ))}
          {onCopyRow && <div style={{ gridRow: 1, gridColumn: cols.length + 2 }} />}
          {eyes.map((eye, r) => {
            const excluded = isExcluded(eye);
            return (
              <div key={`lbl-${eye}`} className={`font-extrabold text-brand-navy py-1 ${excluded ? "opacity-45" : ""}`} style={{ gridRow: r + 2, gridColumn: 1 }}>
                {eye}{excluded && <EyeOff size={11} className="inline ml-1 text-slate-400" />}
              </div>
            );
          })}
          {/* Celle in ordine colonna-major: per ogni campo prima OD poi OS → TAB orizzontale */}
          {cols.map((c, i) =>
            eyes.map((eye, r) => {
              const excluded = isExcluded(eye);
              return (
                <div key={`${c.k}-${eye}`} className={excluded ? "opacity-45" : ""} style={{ gridRow: r + 2, gridColumn: i + 2 }}>
                  <WheelSelect value={getCell(eye)[c.k]} options={c.options}
                    disabled={readOnly || excluded}
                    testid={`${prefix}-${eye.toLowerCase()}-${c.k}`}
                    onChange={(val) => setCell(eye, { [c.k]: val })} />
                </div>
              );
            })
          )}
          {onCopyRow && eyes.map((eye, r) => {
            const other = eye === "OD" ? "OS" : "OD";
            const excluded = isExcluded(eye);
            return (
              <div key={`cp-${eye}`} className="text-center" style={{ gridRow: r + 2, gridColumn: cols.length + 2 }}>
                {!readOnly && !excluded && (
                  <button type="button" title={`Copia riga ${eye} → ${other}`} tabIndex={-1}
                    data-testid={`${prefix}-copy-${eye.toLowerCase()}`}
                    onClick={() => onCopyRow(eye, other)}
                    className="text-brand-azure hover:bg-brand-azure/10 rounded p-1">
                    {eye === "OD" ? <ArrowRight size={13} /> : <ArrowLeft size={13} />}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function YesNo({ value, onChange, readOnly, testid }) {
  return (
    <div className="flex gap-1" data-testid={testid}>
      {[["no", "No"], ["si", "Sì"]].map(([v, lbl]) => (
        <button key={v} type="button" disabled={readOnly} data-testid={`${testid}-${v}`}
          onClick={() => onChange(value === v ? null : v)}
          className={`px-3 py-1 rounded-full border-2 text-[11px] font-bold transition disabled:opacity-50 ${value === v ? (v === "si" ? "bg-brand-orange border-brand-orange text-white" : "bg-emerald-600 border-emerald-600 text-white") : "bg-white border-[#E2E8F0] text-slate-500 hover:border-slate-400"}`}>
          {lbl}
        </button>
      ))}
    </div>
  );
}

const LENTI_COLS = [
  { k: "sph", label: "Sfera", options: SPHERE_OPTIONS },
  { k: "cyl", label: "Cilindro", options: CYL_OPTIONS },
  { k: "axis", label: "Asse", options: AXIS_OPTIONS },
];
const RX_COLS_BASE = [
  { k: "sph", label: "Sfera", options: SPHERE_OPTIONS },
  { k: "cyl", label: "Cilindro", options: CYL_OPTIONS },
  { k: "axis", label: "Asse", options: AXIS_OPTIONS },
  { k: "visus", label: "Visus corretto", options: ACUITY_OPTIONS },
];
const RX_COLS_BY_DIST = {
  lontano: RX_COLS_BASE,
  intermedio: [...RX_COLS_BASE, { k: "add", label: "Add/SF intermedio", options: ADD_OPTIONS }],
  vicino: [...RX_COLS_BASE, { k: "add", label: "Add/SF vicino", options: ADD_OPTIONS }],
};

export default function RefrazioneAvanzataBlock({ prescrizione, setPrescrizione, readOnly, eyeExcluded = {}, part = "all" }) {
  const av = (prescrizione || {}).avanzata || {};
  const [confirmCopy, setConfirmCopy] = useState(null);
  const setAv = (patch) => setPrescrizione({ ...(prescrizione || {}), avanzata: { ...av, ...patch } });
  const isExcluded = (eye) => !!(eyeExcluded[eye] || {}).excluded;

  // nuova prescrizione: av[dist][eye]; lenti in uso: av.lenti_uso[dist][eye]
  const getRx = (dist, eye) => ((av[dist] || {})[eye]) || {};
  const setRx = (dist, eye, patch) => setAv({ [dist]: { ...(av[dist] || {}), [eye]: { ...getRx(dist, eye), ...patch } } });
  const getLu = (dist, eye) => (((av.lenti_uso || {})[dist] || {})[eye]) || {};
  const setLu = (dist, eye, patch) => setAv({ lenti_uso: { ...(av.lenti_uso || {}), [dist]: { ...((av.lenti_uso || {})[dist] || {}), [eye]: { ...getLu(dist, eye), ...patch } } } });

  const copyAll = (from, to, force = false) => {
    if (isExcluded(to) && !force) {
      if (!window.confirm(`${to} è escluso: copiare comunque i valori da ${from}?`)) return;
    }
    const hasTarget = DISTANCES.some((d) => Object.values(getRx(d.k, to)).some((v) => v));
    if (hasTarget && !force) { setConfirmCopy({ from, to }); return; }
    const next = { ...av };
    DISTANCES.forEach((d) => { next[d.k] = { ...(next[d.k] || {}), [to]: { ...getRx(d.k, from) } }; });
    setPrescrizione({ ...(prescrizione || {}), avanzata: next });
    setConfirmCopy(null);
    toast.success(`Prescrizione copiata ${from} → ${to}`);
  };

  const showLenti = part === "lenti" || part === "all";
  const showRx = part === "prescrizione" || part === "all";
  const usaOcchiali = av.usa_occhiali || (av.lenti_in_uso ? "si" : null); // retro-compat: se c'era testo lenti in uso

  return (
    <div className={showRx && !showLenti ? "" : "bg-white border border-[#E2E8F0] rounded-xl p-4 mb-4"} data-testid={showLenti ? "refrazione-lenti-block" : "refrazione-avanzata-block"}>
      {showLenti && (
        <>
          <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
            <div className="flex items-center gap-2">
              <Glasses size={16} className="text-brand-azure" />
              <span className="font-extrabold text-brand-navy text-sm">Utilizza lenti a tempiale / occhiali?</span>
            </div>
            <YesNo value={usaOcchiali} onChange={(v) => setAv({ usa_occhiali: v })} readOnly={readOnly} testid="refadv-usa-occhiali" />
          </div>

          {/* Meta sempre visibile: LAC, dominante, ambliopia */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-3">
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Usa LAC</label>
              <select disabled={readOnly} value={av.usa_lac || ""} onChange={(e) => setAv({ usa_lac: e.target.value })}
                data-testid="refadv-lac" className="mt-1 w-full px-2 py-1.5 rounded border border-[#E2E8F0] text-xs bg-white">
                <option value="">—</option><option value="no">No</option><option value="si">Sì</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Occhio dominante</label>
              <select disabled={readOnly} value={av.occhio_dominante || ""} onChange={(e) => setAv({ occhio_dominante: e.target.value })}
                data-testid="refadv-dominante" className="mt-1 w-full px-2 py-1.5 rounded border border-[#E2E8F0] text-xs bg-white">
                <option value="">Non definito</option><option value="OD">OD</option><option value="OS">OS</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Ambliopia</label>
              <div className="flex gap-1.5 mt-1">
                <select disabled={readOnly} value={(av.ambliopia || {}).v || ""} onChange={(e) => setAv({ ambliopia: { ...(av.ambliopia || {}), v: e.target.value } })}
                  data-testid="refadv-ambliopia" className="px-2 py-1.5 rounded border border-[#E2E8F0] text-xs bg-white">
                  <option value="">Assente</option><option value="OD">OD</option><option value="OS">OS</option><option value="bilaterale">Bilaterale</option>
                </select>
                {(av.ambliopia || {}).v && (
                  <input disabled={readOnly} value={(av.ambliopia || {}).note || ""} onChange={(e) => setAv({ ambliopia: { ...(av.ambliopia || {}), note: e.target.value } })}
                    placeholder="Note" data-testid="refadv-ambliopia-note" className="flex-1 px-2 py-1.5 rounded border border-[#E2E8F0] text-xs min-w-0" />
                )}
              </div>
            </div>
          </div>

          {usaOcchiali === "si" && (
            <div className="border border-brand-azure/25 bg-brand-azure/5 rounded-lg p-3 mb-2" data-testid="lenti-in-uso-block">
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <span className="text-[11px] font-extrabold uppercase tracking-wider text-brand-navy">Lenti in uso</span>
                <input disabled={readOnly} value={av.lenti_in_uso || ""} onChange={(e) => setAv({ lenti_in_uso: e.target.value })}
                  placeholder="Note lenti (es. progressive dal 2023…)" data-testid="refadv-lenti-in-uso"
                  className="flex-1 min-w-[180px] px-2 py-1 rounded border border-[#E2E8F0] text-xs" />
              </div>
              {/* Prescrizione unica lente in uso (dati in av.lenti_uso.lontano — chiave storica, retro-compatibile) */}
              <EyeTable title="Prescrizione lente" cols={LENTI_COLS} readOnly={readOnly} isExcluded={isExcluded}
                prefix="lenti-lontano"
                getCell={(eye) => getLu("lontano", eye)} setCell={(eye, patch) => setLu("lontano", eye, patch)}
                onCopyRow={(from, to) => {
                  if (isExcluded(to)) { toast.error(`${to} è escluso`); return; }
                  setLu("lontano", to, { ...getLu("lontano", from) });
                }} />
              {/* ADD/SF per la correzione da vicino */}
              <div className="mb-2" data-testid="lenti-add-block">
                <div className="text-[11px] font-extrabold uppercase tracking-wider text-brand-navy mb-1">ADD/SF (correzione da vicino)</div>
                <div className="grid grid-cols-2 gap-3 max-w-md">
                  {["OD", "OS"].map((eye) => (
                    <div key={eye} className="flex items-center gap-2">
                      <span className={`font-extrabold text-xs ${isExcluded(eye) ? "text-slate-300" : "text-brand-navy"}`}>{eye}</span>
                      <WheelSelect value={getLu("lontano", eye).add || ""} options={ADD_OPTIONS}
                        disabled={readOnly || isExcluded(eye)} testid={`lenti-add-${eye.toLowerCase()}`}
                        onChange={(val) => setLu("lontano", eye, { add: val })} />
                    </div>
                  ))}
                </div>
              </div>
              {/* Retro-compatibilità: eventuali dati storici nelle vecchie righe Intermedio/Vicino restano visibili */}
              {(() => {
                const legacy = ["intermedio", "vicino"].flatMap((dist) =>
                  ["OD", "OS"].flatMap((eye) => {
                    const c = getLu(dist, eye);
                    const vals = ["sph", "cyl", "axis", "add"].map((k) => c[k]).filter(Boolean);
                    return vals.length ? [`${dist} ${eye}: ${["sph", "cyl", "axis", "add"].filter((k) => c[k]).map((k) => `${k} ${c[k]}`).join(", ")}`] : [];
                  })
                );
                return legacy.length ? (
                  <div className="text-[10px] text-slate-600 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 mb-2" data-testid="lenti-legacy-note">
                    <b>Dati precedenti (righe Intermedio/Vicino):</b>
                    {legacy.map((l, i) => <div key={i}>{l}</div>)}
                  </div>
                ) : null;
              })()}
              <div className="border-t border-brand-azure/20 pt-2 mt-1" data-testid="csl-block">
                <div className="text-[11px] font-extrabold uppercase tracking-wider text-brand-navy mb-1">Visus con sue lenti (CSL)</div>
                <div className="grid gap-x-2 gap-y-1.5 items-center max-w-md text-xs" style={{ gridTemplateColumns: "36px 1fr 1fr" }}>
                  <div />
                  <div className="text-center text-slate-500 text-[10px] uppercase tracking-wider font-bold">PL (lontano)</div>
                  <div className="text-center text-slate-500 text-[10px] uppercase tracking-wider font-bold">PV (vicino)</div>
                  {["OD", "OS"].map((eye) => {
                    const csl = av.csl || {};
                    const legacyFlat = typeof csl[eye] === "string" ? csl[eye] : "";
                    const plVal = ((csl.lontano || {})[eye]) ?? legacyFlat;
                    const pvVal = ((csl.vicino || {})[eye]) || "";
                    const setCsl = (dist, val) => {
                      const cur = av.csl || {};
                      const next = { ...cur, [dist]: { ...(cur[dist] || {}), [eye]: val } };
                      if (dist === "lontano") next[eye] = val;
                      setAv({ csl: next });
                    };
                    return (
                      <React.Fragment key={eye}>
                        <span className={`font-extrabold text-xs ${isExcluded(eye) ? "text-slate-300" : "text-brand-navy"}`}>{eye}</span>
                        <input disabled={readOnly || isExcluded(eye)} data-testid={`csl-${eye.toLowerCase()}-pl`}
                          value={plVal || ""} onChange={(e) => setCsl("lontano", e.target.value)} placeholder="es. 10/10"
                          className="w-full px-2 py-1.5 rounded border border-[#E2E8F0] text-xs text-center bg-white focus:outline-none focus:border-brand-azure disabled:bg-slate-100 disabled:text-slate-400" />
                        <input disabled={readOnly || isExcluded(eye)} data-testid={`csl-${eye.toLowerCase()}-pv`}
                          value={pvVal} onChange={(e) => setCsl("vicino", e.target.value)} placeholder="es. J1"
                          className="w-full px-2 py-1.5 rounded border border-[#E2E8F0] text-xs text-center bg-white focus:outline-none focus:border-brand-azure disabled:bg-slate-100 disabled:text-slate-400" />
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
          {usaOcchiali === "no" && (
            <div className="text-[11px] text-slate-500 italic mb-1" data-testid="lenti-no-hint">Nessuna lente in uso. L&apos;Autoref qui sotto resta sempre disponibile.</div>
          )}
        </>
      )}

      {showRx && (
        <div className={showLenti ? "border-t border-slate-100 pt-3 mt-3" : "bg-white border border-[#E2E8F0] rounded-xl p-4 mb-4"} data-testid="nuova-prescrizione-block">
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <span className="w-1 h-5 bg-brand-azure rounded-full"></span>
            <h3 className="font-extrabold text-brand-navy text-base">Prescrizione Occhiali</h3>
            <span className="text-[10px] uppercase tracking-wider bg-brand-orange/10 text-brand-orange px-2 py-0.5 rounded font-bold">Notazione assi: TABO</span>
            {!readOnly && (
              <button type="button" tabIndex={-1} data-testid="refadv-copy-all-od-os" onClick={() => copyAll("OD", "OS")}
                className="ml-auto inline-flex items-center gap-1.5 bg-brand-azure hover:bg-brand-navy text-white text-xs font-bold px-3 py-1.5 rounded-lg transition">
                <ArrowRight size={13} /> Copia tutto OD → OS
              </button>
            )}
          </div>
          {DISTANCES.map((d) => (
            <EyeTable key={d.k} title={d.label} cols={RX_COLS_BY_DIST[d.k]} readOnly={readOnly} isExcluded={isExcluded}
              prefix={`refadv-${d.k}`}
              getCell={(eye) => getRx(d.k, eye)} setCell={(eye, patch) => setRx(d.k, eye, patch)}
              onCopyRow={(from, to) => {
                if (isExcluded(to)) { toast.error(`${to} è escluso`); return; }
                setRx(d.k, to, { ...getRx(d.k, from) });
              }} />
          ))}
          {/* Retro-compatibilità: eventuale Add/SF storico sulla riga Lontano resta visibile */}
          {(() => {
            const legacy = ["OD", "OS"].filter((eye) => getRx("lontano", eye).add);
            return legacy.length ? (
              <div className="text-[10px] text-slate-600 bg-amber-50 border border-amber-200 rounded px-2 py-1.5" data-testid="rx-lontano-add-legacy">
                <b>Add/SF storico (riga Lontano):</b> {legacy.map((eye) => `${eye} ${getRx("lontano", eye).add}`).join(" · ")}
              </div>
            ) : null;
          })()}
        </div>
      )}

      {confirmCopy && (
        <div className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center p-4" data-testid="refadv-copy-confirm">
          <div className="bg-white rounded-2xl max-w-sm w-full p-6 shadow-2xl">
            <p className="text-sm text-brand-navy font-semibold">
              L&apos;occhio {confirmCopy.to} ha già valori compilati. Sovrascriverli con quelli di {confirmCopy.from}?
            </p>
            <div className="flex justify-end gap-2 mt-4">
              <button data-testid="refadv-copy-cancel" onClick={() => setConfirmCopy(null)} className="px-4 py-2 rounded-lg text-sm font-bold text-slate-600 hover:bg-slate-100">Annulla</button>
              <button data-testid="refadv-copy-ok" onClick={() => copyAll(confirmCopy.from, confirmCopy.to, true)} className="px-4 py-2 rounded-lg text-sm font-bold bg-brand-azure text-white">Sovrascrivi</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
